import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Check } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const SectionHeading = React.memo(({ title, highlight, color, label = "Discovery", description, centered = false, wins = [], theme = 'dark', hideTitle = false }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const els = container.querySelectorAll('[data-sh]');
    if (els.length === 0) return;

    const ctx = gsap.context(() => {
      // Small timeout to ensure page layout (Lenis/Fonts) is stable
      setTimeout(() => {
        const isMobile = window.innerWidth < 768;
        const yOffset = isMobile ? 30 : 60;
        const startPoint = isMobile ? 'top 110%' : 'top 98%';
        const endPoint = isMobile ? 'top 40%' : 'top 40%';

        // Set INITIAL state of elements to avoid the jump
        gsap.set(els, { 
          opacity: 0, 
          y: yOffset, 
          scale: isMobile ? 1 : 0.98,
          visibility: 'visible' 
        });

        // Set container to visible once elements are prepared
        gsap.set(container, { visibility: 'visible', opacity: 1 });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: container,
            start: startPoint,
            end: endPoint,
            scrub: isMobile ? 0.8 : 1.2,
            invalidateOnRefresh: true
          },
        });

        // Fade In Phase
        tl.to(els, {
          opacity: 1,
          y: 0,
          scale: 1,
          stagger: 0.15,
          ease: 'power1.out',
          duration: 0.6
        });
      }, 150);
    }, container);

    return () => ctx.revert();
  }, [title, highlight, label, description, wins, color, centered, theme]);

  return (
    <div
      ref={containerRef}
      className={`mb-20 ${centered ? 'text-center flex flex-col items-center' : ''}`}
      style={{ opacity: 0, visibility: 'hidden' }}
    >
      <div data-sh className={`flex items-center gap-3 mb-4 ${centered ? 'justify-center' : ''}`}>
        <span className="w-[5px] h-[5px] flex-shrink-0" style={{ backgroundColor: color }} />
        <div className="w-8 h-px opacity-40" style={{ backgroundColor: color }} />
        <span className="font-mono-swiss text-[9px] md:text-[10px] font-semibold uppercase tracking-[0.3em] text-text-muted whitespace-nowrap">{label}</span>
        <div className={`h-px opacity-40 ${centered ? 'w-8' : 'flex-1 max-w-24'}`} style={{ backgroundColor: color }} />
        {centered && <span className="w-[5px] h-[5px] flex-shrink-0" style={{ backgroundColor: color }} />}
      </div>
      {!hideTitle && (
        <div className="mt-3 mb-4 perspective-1000">
          <h4 data-sh className="hero-h4 block mb-1 font-black leading-[0.95] tracking-tight [text-shadow:none]">
            {title}
          </h4>
          <h2
            data-sh
            className="block font-black leading-[0.95] md:leading-[0.8] uppercase tracking-tight [text-shadow:none] audiowide-regular"
            style={{ color, fontSize: 'clamp(58px, 11vw, 216px)' }}
          >
            {highlight}
          </h2>
        </div>
      )}
      {description && (
        <p data-sh className={`text-text-muted font-light leading-relaxed w-[90%] max-w-[1200px] ${centered ? 'mx-auto' : ''} mb-12`}>
          {description}
        </p>
      )}

      {wins && wins.length > 0 && (
        <div
          data-sh
          className={`flex flex-wrap gap-x-6 gap-y-3 mt-8 ${centered ? 'justify-center' : 'justify-start'}`}
        >
          {wins.map((win) => (
            <div key={win} className="flex items-center gap-2">
              <Check size={12} style={{ color }} strokeWidth={3} />
              <span className="text-xs font-black tracking-[0.2em] uppercase">{win}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export default SectionHeading;
